import { ProductSchema } from "@labeling-tools/api/atlas";
import type { CoverageBetaQaReason } from "@/types/coverage";
import type { GroupImage } from "@/utils/groupedImages";
import type {
  ReactZoomPanPinchContentRef,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { omit } from "lodash";
import { create } from "zustand";

/** Image views that support a brightness adjustment (not the planogram). */
export type BrightnessView = "query" | "reference";

/** Bounds for the per-view brightness adjustment (%). */
export const BRIGHTNESS_MIN = 20;
export const BRIGHTNESS_MAX = 200;

const clampBrightness = (value: number) =>
  Math.min(BRIGHTNESS_MAX, Math.max(BRIGHTNESS_MIN, value));

/**
 * A product attached to an empty area. Discriminated by `source`:
 * - "reference": picked from a reference-image detection box (carries the
 *   reference image + detection indices).
 * - "planogram": picked off the planogram viewer (carries its planogram_id).
 */
type ReferenceSelectedProduct = ProductSchema & {
  source: "reference";
  referenceProductDetectionIdx: number;
  referenceImgIdx: number;
  planogram_id?: string;
  /**
   * The product the model originally assigned to this detection (before the
   * confirm step). The user can overwrite the product via the confirm step, but
   * we keep the original so the payload's `identified_product_id` still records
   * what was detected.
   */
  identifiedProductId?: string;
  /**
   * Whether the user explicitly picked this product in the confirm step (vs. the
   * provisional model default added when the detection is first clicked). Drives
   * the confirm panel's green check so nothing shows as confirmed until chosen.
   */
  confirmed?: boolean;
};
type PlanogramSelectedProduct = ProductSchema & {
  source: "planogram";
  planogram_id: string;
};
export type SelectedProduct =
  | ReferenceSelectedProduct
  | PlanogramSelectedProduct;

export interface FeEmptyArea {
  id: string;
  points: number[][];
  class_index: number;
  confidence: number;
  // above from parcel response
  source: "human" | "auto";
  feEmptyAreaIdx: number;
  isDeleted: boolean;
  selectedProducts: SelectedProduct[];
  isHidden: boolean;
  isValid: boolean;
  /**
   * The user-confirmed authoritative product for this area (picked in the
   * confirm step). Overwrites `confirmation_product_id` at submit. Null when the
   * user hasn't confirmed / picked "no match".
   */
  confirmedProductId?: string | null;
  /**
   * Set when the area was pre-filled from another task's labels (SAM2
   * propagation): what was propagated, to measure what the labeler changed.
   */
  propagated?: PropagatedOrigin;
  /**
   * Sticky: set the first time the labeler changes a propagated area's products
   * or decision, and never cleared — so undoing an edit (pick a product, then
   * remove it) leaves a box the labeler touched, not a fresh SAM 2 suggestion.
   * Set by `flagSuggestionEdit`.
   */
  edited?: boolean;
  /**
   * Grouped query images: the view this box was drawn on. Absent/null means
   * the primary view — auto detections always are, since CV only runs there.
   */
  imageId?: string | null;
}

export interface PropagatedOrigin {
  sourceTaskId: string;
  areaId: string;
  points: number[][];
  productIds: string[];
}

export interface IdxToFeEmptyArea {
  [id: string]: FeEmptyArea;
}

/**
 * An empty area is "reviewed" — ready to submit and no longer blocking new
 * drawing — when it has been marked valid AND every reference-sourced product
 * has been explicitly confirmed in the confirm step. Planogram picks are direct
 * selections, so they need no confirmation.
 */
export const isEmptyAreaReviewed = (area: FeEmptyArea): boolean =>
  area.isValid &&
  area.selectedProducts.every(
    (p) => p.source !== "reference" || p.confirmed === true,
  );

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length &&
  [...a].sort().join("\n") === [...b].sort().join("\n");

/**
 * A SAM 2 suggestion the labeler hasn't changed: pre-filled from another
 * task's labels (`propagated`), not yet decided (`!isValid`), and holding
 * exactly the products it arrived with. None means the original area was
 * "No product match" — a submitted area with no products can only be one,
 * since submit needs every box decided. Submit accepts it as is (submitting
 * is the confirmation); until then it renders violet, not green. Picking a
 * product or No match decides it (`isValid`); any edit (`edited`, even one
 * since undone), or a product that didn't load, makes it an ordinary undecided
 * box.
 */
export const isUnconfirmedSuggestion = (area: FeEmptyArea): boolean =>
  area.propagated != null &&
  !area.edited &&
  !area.isDeleted &&
  !area.isValid &&
  sameIds(
    area.selectedProducts.map((p) => p.id ?? ""),
    area.propagated.productIds,
  );

/**
 * `after` with `edited` set when it's a SAM 2 suggestion whose products or
 * decision changed from `before`. A re-prefill replaces `propagated`, so it
 * doesn't count as an edit.
 */
const flagSuggestionEdit = (
  before: FeEmptyArea | undefined,
  after: FeEmptyArea,
): FeEmptyArea =>
  after.propagated != null &&
  !after.edited &&
  before?.propagated === after.propagated &&
  (before.selectedProducts !== after.selectedProducts ||
    before.isValid !== after.isValid)
    ? { ...after, edited: true }
    : after;

const flagSuggestionEdits = (
  before: IdxToFeEmptyArea,
  after: IdxToFeEmptyArea,
): IdxToFeEmptyArea => {
  let next = after;
  for (const key of Object.keys(after)) {
    const flagged = flagSuggestionEdit(before[key], after[key]);
    if (flagged === after[key]) continue;
    if (next === after) next = { ...after };
    next[key] = flagged;
  }
  return next;
};

/** What submit requires of every box it sends: decided, or an unchanged SAM 2 suggestion. */
export const isEmptyAreaSubmittable = (area: FeEmptyArea): boolean =>
  isEmptyAreaReviewed(area) || isUnconfirmedSuggestion(area);

/**
 * Which reference detection is being confirmed. The confirm step's candidates
 * come from that detection's `product_ids_top_n`, and the pick is stored on the
 * `emptyAreaIdx`.
 */
export interface ConfirmingContext {
  emptyAreaIdx: number;
  referenceImgIdx: number;
  referenceProductDetectionIdx: number;
}

/** A human-drawn unblurred-person box. Serialized into `unblurred_persons`. */
export interface FePerson {
  id: string;
  points: number[][];
  confidence: number;
  class_index: number;
  isDeleted: boolean;
  /** Grouped query images: the view this box was drawn on (null = primary). */
  imageId?: string | null;
}

/**
 * Identifies a single reviewable item in Coverage Beta QA — an empty area
 * (keyed by its stable `feEmptyAreaIdx`) or a person box (keyed by its index
 * in the `persons` array). A string union (rather than a raw number) so an
 * area and a person can never collide on the same `qaVerdicts` key. See
 * `getOrderedQaItems` (utils/build-coverage-beta-qa-result.ts).
 */
export type QaItemKey = `area:${number}` | `person:${number}`;

const initialState = {
  // When true the task is being viewed (admin/QA) as it was completed — every
  // mutating action below no-ops so nothing can be edited. Set via
  // `hydrateReadOnly`, cleared by `reset()`.
  isReadOnly: false,
  activeProductId: "",
  // product_ids of the detections neighbouring the currently-hovered empty area.
  // Used to highlight those same products on the reference image and planogram so
  // the labeler can locate the region without panning.
  neighbourProductIds: [] as string[],
  // product_id of the planogram cell currently under the cursor. Drives the
  // reverse locate cue: hovering a product on the planogram teal-highlights its
  // detection box(es) on the query image, so the labeler can find that exact
  // facing on a shelf full of near-identical packs.
  //
  // Its own field rather than a second writer on activeProductId: that one feeds
  // the planogram viewer's activeProductIds, which re-sorts paint order. Writing
  // it from the planogram would remerge the placed list and re-sort cells under
  // the cursor on every cell crossed. Nothing on the planogram reads this one, so
  // a hover re-renders exactly the boxes whose answer changed.
  locatedProductId: "",
  // A clicked product box (query image or a context frame) asking the
  // planogram to frame that product. One-shot: the planogram view consumes it.
  // `nonce` lets re-clicking the same product frame it again.
  planogramFocusRequest: null as { productId: string; nonce: number } | null,
  // Which view the mouse is over — routes the "0" reset hotkey to the right
  // zoom/pan controller (query image / reference image / planogram).
  hoveredView: "query" as "query" | "reference" | "planogram",
  // CSS brightness (%) applied per image view, so a labeler can brighten a dark
  // query or reference photo independently. 100 = unmodified. Adjusted via the
  // brightness hotkeys (act on the hovered view) and the query toolbar slider.
  brightnessByView: { query: 100, reference: 100 } as Record<
    "query" | "reference",
    number
  >,
  // Bumped to re-fit the planogram — its "reset view".
  planogramResetNonce: 0,
  // Per-action counts of hotkey vs manual-click usage, recorded into
  // result.metadata to measure hotkey adoption (see recordInteraction).
  interactionCounts: {} as Record<string, { hotkey: number; click: number }>,
  // Per-task counts of confirm-flow / rework events (confirm_pick,
  // confirm_no_match, confirm_cancel, confirm_reopen, product_removed).
  // Recorded into result.metadata to gauge how confusing the confirm flow is.
  eventCounts: {} as Record<string, number>,
  activeEmptyAreaIdx: 0,
  idxToFeEmptyArea: {} as IdxToFeEmptyArea,
  // The reference detection whose product selection is awaiting confirmation
  // (drives the inline confirm step); null when closed.
  confirming: null as ConfirmingContext | null,
  // Bumped whenever the user attempts an action that's blocked because a
  // confirm step is open. The confirm panel watches it to flash/shake and pull
  // attention to the decision it needs (instead of a silent cursor-not-allowed).
  confirmNudge: 0,
  persons: [] as FePerson[],
  activeRefImgIdx: 0,
  // Total number of reference images (from the parcel). Lets the next/prev
  // hotkeys clamp `activeRefImgIdx` without the store needing the parcel.
  referenceImgCount: 0,
  activeMode: "cursor" as "cursor" | "draw-box" | "draw-person",
  // Which panel the right pane shows. Lifted out of the panel's local state so
  // a hotkey (`p`) can toggle it.
  rightPanelView: "shelf-images" as "shelf-images" | "planogram",
  // Grouped query images: the view the query pane is showing (null = the
  // primary). New boxes are drawn onto it.
  activeImageId: null as string | null,
  // An area selected while it sat on another view: the query pane frames it
  // once that view's image has painted (its box can't be measured before).
  pendingAreaFocusIdx: null as number | null,
  // Show all overlay boxes on the images: the model's product detections plus
  // the labeler's empty-area and person boxes. Toggled off (hotkey `b` / the
  // eye button) to declutter and see the underlying image cleanly.
  showBoxes: true,
  // Planogram selection state (base ids per aisle, user overrides/extras, the
  // active tab, and which aisle/slot each empty area belongs to — keyed by the
  // stable feEmptyAreaIdx).
  basePlanogramIds: [] as string[],
  overridePlanogramIds: {} as Record<number, string>,
  extraPlanogramIds: [] as string[],
  activePlanogramTab: 0,
  boxIdxToSlot: {} as Record<number, number>,
  // Coverage Beta QA (audit-only review of a completed submission). Set via
  // `setQaMode` alongside `hydrateReadOnly` — see CoverageBetaTask's qaMode
  // branch. Boxes stay read-only (isReadOnly), but a QA reviewer edits these
  // instead: per-item reason tags (keyed by QaItemKey) and the task-level
  // fields.
  isQaMode: false,
  qaVerdicts: {} as Record<QaItemKey, CoverageBetaQaReason[] | undefined>,
  // Per-box free-text notes, same keying as `qaVerdicts` but a separate slice:
  // a note is optional commentary, never a verdict, so it must survive the
  // reviewer clearing their reason chips (which resets that box's verdict to
  // `undefined`) and must never gate submit. See `isQaReadyToSubmit`.
  qaBoxNotes: {} as Record<QaItemKey, string>,
  // Which item (area or person) the QA panel's pager currently has open —
  // drives the pulse/highlight on canvas and in the sidebar. Separate from
  // `activeEmptyAreaIdx` (which stays reserved for the normal draw/edit flow)
  // since a person box has no `feEmptyAreaIdx` to reuse.
  activeQaItemKey: null as QaItemKey | null,
  qaTaskLevelCorrections: {
    unblurredPersonsMissed: false,
    boxesShouldHaveBeenDrawn: 0,
    qaFeedback: "",
    flagLabeler: false,
  },
  // Editable Coverage QA (a whole-review COVERAGE_REVIEW_QA task, see
  // `hydrateEditable`): the labeler's submission as first hydrated, kept to
  // badge what the QA reviewer changed and to revert a box to it. Null
  // everywhere else — which is also how components tell they're in that flow.
  qaEditOriginal: null as QaEditOriginal | null,
  // Optional task-level feedback the QA reviewer leaves for the labeler.
  qaEditNote: "",
  qaEditFlagLabeler: false,
};

/** The labeler's submission as hydrated into an editable QA task. */
export interface QaEditOriginal {
  idxToFeEmptyArea: IdxToFeEmptyArea;
  boxIdxToSlot: Record<number, number>;
}

type IS = typeof initialState;

type State = IS & {
  setActiveProductId: (activeProductId: IS["activeProductId"]) => void;
  /** Highlight the detections neighbouring a hovered empty area (or clear with []). */
  setNeighbourProductIds: (
    neighbourProductIds: IS["neighbourProductIds"],
  ) => void;
  /** Locate a planogram-hovered product on the query image (or clear with ""). */
  setLocatedProductId: (locatedProductId: IS["locatedProductId"]) => void;
  /** Ask the planogram (when it's the shown view) to frame a product. */
  requestPlanogramFocus: (productId: string) => void;
  clearPlanogramFocusRequest: () => void;
  setHoveredView: (hoveredView: IS["hoveredView"]) => void;
  /**
   * The active reference image's pan/zoom controller, registered by whichever
   * reference image is currently visible. Used by the zoom + reset hotkeys when
   * the mouse is over the reference view.
   */
  referencePinchRef: ReactZoomPanPinchContentRef | null;
  setReferencePinchRef: (
    referencePinchRef: ReactZoomPanPinchContentRef | null,
  ) => void;
  /**
   * The (base, non-reusable) planogram viewer's pan/zoom controller, registered
   * while that viewer is mounted. Lets the zoom hotkeys act on the planogram
   * when the mouse is over it.
   */
  planogramPinchRef: ReactZoomPanPinchRef | null;
  setPlanogramPinchRef: (
    planogramPinchRef: ReactZoomPanPinchRef | null,
  ) => void;
  /** Reset (re-fit) the planogram view. */
  resetPlanogramView: () => void;
  /** Set an image view's brightness (%), clamped to [BRIGHTNESS_MIN, BRIGHTNESS_MAX]. */
  setBrightness: (view: BrightnessView, value: number) => void;
  /** Nudge an image view's brightness by `delta` (%), clamped. */
  adjustBrightness: (view: BrightnessView, delta: number) => void;
  /** Reset an image view's brightness back to 100%. */
  resetBrightness: (view: BrightnessView) => void;
  /** Record one use of an action via hotkey or manual click (for analytics). */
  recordInteraction: (action: string, source: "hotkey" | "click") => void;
  /** Increment a per-task workflow/rework event counter (for analytics). */
  recordEvent: (name: string) => void;
  setActiveEmptyAreaIdx: (activeEmptyAreaIdx: IS["activeEmptyAreaIdx"]) => void;
  /** Move the active area to the previous/next non-deleted empty area. */
  prevEmptyArea: () => void;
  nextEmptyArea: () => void;
  setIdxToFeEmptyArea: (idxToFeEmptyArea: IS["idxToFeEmptyArea"]) => void;
  setActiveRefImgIdx: (activeRefImgIdx: IS["activeRefImgIdx"]) => void;
  setReferenceImgCount: (referenceImgCount: IS["referenceImgCount"]) => void;
  /** Move to the previous/next reference image, clamped to the valid range. */
  prevRefImg: () => void;
  nextRefImg: () => void;
  setActiveMode: (activeMode: IS["activeMode"]) => void;
  setRightPanelView: (rightPanelView: IS["rightPanelView"]) => void;
  /** Flip the right panel between the shelf-images and planogram views. */
  toggleRightPanelView: () => void;
  /**
   * Whether the task has shelf (reference) images. Pilot tasks don't, so the
   * right panel drops the shelf-images tab and shows the planogram — see
   * `selectRightPanelView`. Kept out of `initialState` so the mid-load
   * `reset()`s don't flash the tab back between tasks; CoverageBetaTask sets
   * it once each parcel loads. Defaults to true (the standard flow).
   */
  hasShelfImages: boolean;
  setHasShelfImages: (hasShelfImages: boolean) => void;
  /**
   * Admin view: draw SAM 2's guesses over the frames, next to the labelers'
   * boxes. Off by default; kept out of `initialState` so it stays as the
   * admin left it while paging through tasks.
   */
  showSam2Boxes: boolean;
  toggleSam2Boxes: () => void;
  /**
   * Grouped query images for the current task, primary first, plus the
   * primary's id. Empty for an ungrouped task. Out of `initialState` for the
   * same reason as `hasShelfImages`; set by CoverageBetaTask per parcel.
   */
  groupImages: GroupImage[];
  primaryImageId: string;
  setImageGroup: (primaryImageId: string, groupImages: GroupImage[]) => void;
  /** Show a grouped view in the query pane (null = primary). */
  setActiveImageId: (activeImageId: IS["activeImageId"]) => void;
  clearPendingAreaFocus: () => void;
  setShowBoxes: (showBoxes: IS["showBoxes"]) => void;
  /** Show/hide all overlay boxes (detections + empty-area + person). */
  toggleBoxes: () => void;
  /**
   * Append a human-drawn empty area. `points` are normalized (0-1) image
   * coordinates. Returns the index assigned to the new area (also made active).
   */
  addFeEmptyArea: (points: number[][], opts?: { class_index?: number }) => void;
  /**
   * Soft-delete an empty area: moves it to the "Deleted" section (revertable)
   * for both auto and human sources. On submit, auto detections are reported as
   * `deleted_empty_areas`; human-drawn areas simply drop out of the payload.
   */
  deleteFeEmptyArea: (feEmptyAreaIdx: number) => void;
  /**
   * Toggle an empty area's "no match" state: mark it reviewed (`isValid`) with
   * no product, or clear that decision. Submits as an `empty_area` category
   * with no product selection.
   */
  toggleEmptyAreaNoMatch: (feEmptyAreaIdx: number) => void;
  /** Append a human-drawn person box. `points` are normalized (0-1) coords. */
  addPerson: (points: number[][], opts?: { class_index?: number }) => void;
  /** Soft-delete / restore a person box (revertable, within the person list). */
  setPersonDeleted: (id: string, isDeleted: boolean) => void;
  /** Replace a person box's points (after a drag/resize). */
  updatePersonPoints: (id: string, points: number[][]) => void;
  /**
   * The query image's zoom/pan controller. Kept out of `initialState` so it
   * survives `reset()` (the TransformWrapper's `onInit` only fires once).
   */
  pinchRef: ReactZoomPanPinchRef | null;
  setPinchRef: (pinchRef: ReactZoomPanPinchRef | null) => void;
  /**
   * True while the onboarding tutorial overlay is open. Read by
   * useCoverageBetaHotkeys (suppresses every hotkey — the tutorial is fully
   * modal), the planogram viewer's `enableZoom`, and the activity tracker's
   * pause logic. Kept out of `initialState` — unlike `pinchRef`, this value
   * isn't gone forever if cleared (the auto-open effect or the header's
   * replay button can always set it again); the actual risk is a same-task
   * race, since `reset()` runs 2-3 times during a normal task's own initial
   * load (parcel_id / parcelIsLoading transitions in CoverageBetaTask's seed
   * effect) and the replay button isn't gated on that load completing.
   * Closing on a *new* task loading is handled explicitly by the tutorial's
   * own host (its taskId-watching effect), independent of `reset()`.
   */
  tutorialActive: boolean;
  setTutorialActive: (tutorialActive: boolean) => void;
  // --- planogram selection ---
  setBasePlanogramIds: (basePlanogramIds: IS["basePlanogramIds"]) => void;
  setActivePlanogramTab: (activePlanogramTab: IS["activePlanogramTab"]) => void;
  /** Bind an empty area (by stable feEmptyAreaIdx) to an aisle/slot index. */
  assignBoxToSlot: (feEmptyAreaIdx: number, slot: number) => void;
  /** Open the confirm-product step for a reference detection (null closes it). */
  setConfirming: (confirming: ConfirmingContext | null) => void;
  /**
   * Signal that a blocked action was attempted while a confirm step is open, so
   * the confirm panel can draw attention to itself. No-ops when not confirming.
   */
  nudgeConfirm: () => void;
  /**
   * "No match" in the confirm step: remove the reference product currently
   * being confirmed from its area's selection and close the step.
   */
  rejectConfirmingSelection: () => void;
  /**
   * Cancel (✕) the confirm step: undo the selection being confirmed — remove
   * the product and, if the area is left empty, revert it to un-reviewed
   * (unlike "No match", which marks it reviewed).
   */
  cancelConfirmingSelection: () => void;
  /**
   * Confirm the correct product for the reference detection currently being
   * confirmed: overwrite that detection's selected product with `product`
   * (keeping the detection indices + original `identifiedProductId`), record it
   * as the area's `confirmedProductId`, and close the confirm step.
   */
  confirmReferenceProduct: (
    feEmptyAreaIdx: number,
    product: ProductSchema,
  ) => void;
  /**
   * Apply edits from the planogram search modal: replace an existing slot's
   * planogram (records an override + clears that slot's planogram-sourced
   * selections) or append a new planogram (aisleIndex === -1).
   */
  applyPlanogramModalEdits: (
    slots: { aisleIndex: number; planogramId: string }[],
  ) => void;
  setIsReadOnly: (isReadOnly: IS["isReadOnly"]) => void;
  /**
   * Load a completed task's state for read-only viewing (admin) in one shot and
   * flip `isReadOnly` on. Bypasses the per-action guards (it IS the hydration),
   * so it must be the single entry point for seeding a read-only view.
   */
  hydrateReadOnly: (payload: {
    idxToFeEmptyArea: IdxToFeEmptyArea;
    persons: FePerson[];
    basePlanogramIds: string[];
    overridePlanogramIds: Record<number, string>;
    extraPlanogramIds: string[];
    boxIdxToSlot: Record<number, number>;
    activeEmptyAreaIdx: number;
  }) => void;
  /**
   * Load a labeler's completed submission into an editable QA task: the same
   * state `hydrateReadOnly` loads, but every labeling tool stays enabled so the
   * QA reviewer can fix boxes, products and planograms directly. Keeps a copy
   * as `qaEditOriginal` for change badges and per-box revert.
   */
  hydrateEditable: (payload: Parameters<State["hydrateReadOnly"]>[0]) => void;
  /**
   * Put an area back the way the labeler submitted it. An area the QA
   * reviewer drew has nothing to go back to, so it's removed.
   */
  revertAreaToLabeler: (feEmptyAreaIdx: number) => void;
  setQaEditNote: (qaEditNote: IS["qaEditNote"]) => void;
  setQaEditFlagLabeler: (qaEditFlagLabeler: IS["qaEditFlagLabeler"]) => void;
  /** Flip Coverage Beta QA mode on/off. Called alongside `hydrateReadOnly` — see CoverageBetaTask. */
  setQaMode: (isQaMode: IS["isQaMode"]) => void;
  /** Preload verdict state (admin browsing an already-completed QA task). */
  hydrateQaVerdicts: (
    qaVerdicts: IS["qaVerdicts"],
    qaBoxNotes: IS["qaBoxNotes"],
    qaTaskLevelCorrections: IS["qaTaskLevelCorrections"],
  ) => void;
  /** Which QA item the pager currently has open — see `activeQaItemKey`. */
  setActiveQaItemKey: (activeQaItemKey: IS["activeQaItemKey"]) => void;
  /**
   * Mark an empty area active — in QA mode that means `activeQaItemKey`, and
   * everywhere else the normal draw/edit flow's `activeEmptyAreaIdx`. Lets
   * click handlers (QueryEmptyAreaDetectionBox, SummaryRow) call one action
   * instead of each branching on `isQaMode` themselves.
   */
  setActiveArea: (feEmptyAreaIdx: number) => void;
  /**
   * Set an item's flagged reasons. Empty array = reviewer explicitly marked it
   * correct (only CoverageBetaQaPanel's "Mark as Correct" button does this);
   * `undefined` = not (or no longer) reviewed — passed when deselecting the
   * last reason chip, so that alone never counts as marking an item correct.
   */
  setQaBoxReasons: (
    key: QaItemKey,
    reasons: CoverageBetaQaReason[] | undefined,
  ) => void;
  /** Set an item's free-text note. `""` (the default) means no note. */
  setQaBoxNote: (key: QaItemKey, note: string) => void;
  setQaTaskLevelCorrections: (
    partial: Partial<IS["qaTaskLevelCorrections"]>,
  ) => void;
  reset: () => void;
};

/** Return a copy of `counts` with `name` incremented by one. */
const incEvent = (
  counts: Record<string, number>,
  name: string,
): Record<string, number> => ({ ...counts, [name]: (counts[name] ?? 0) + 1 });

/**
 * Step the active empty area to the previous (`dir === -1`) or next (`dir === 1`)
 * non-deleted area, ordered by index and clamped at the ends. No-op while a
 * confirm step is open (switching areas mid-confirm could land a pick on the
 * wrong area — mirrors the summary-row click guard).
 */
const stepEmptyArea = (state: State, dir: 1 | -1): Partial<State> => {
  if (state.confirming != null) return {};
  const indices = Object.values(state.idxToFeEmptyArea)
    .filter((a) => !a.isDeleted)
    .map((a) => a.feEmptyAreaIdx)
    .sort((a, b) => a - b);
  if (indices.length === 0) return {};
  const pos = indices.indexOf(state.activeEmptyAreaIdx);
  const nextPos =
    pos === -1
      ? dir === 1
        ? 0
        : indices.length - 1
      : Math.min(indices.length - 1, Math.max(0, pos + dir));
  const nextIdx = indices[nextPos];
  return nextIdx === state.activeEmptyAreaIdx
    ? {}
    : { activeEmptyAreaIdx: nextIdx, ...showAreaView(state, nextIdx) };
};

/**
 * Switch the query pane to `imageId` (null or the primary's id = primary),
 * queueing a focus on `focusAreaIdx` for once that view has painted. No-op
 * when it's already shown.
 */
const showView = (
  state: State,
  imageId: string | null,
  focusAreaIdx: number | null,
): Partial<State> => {
  const target = imageId === state.primaryImageId ? null : imageId;
  if (target === state.activeImageId) return {};
  return { activeImageId: target, pendingAreaFocusIdx: focusAreaIdx };
};

/**
 * Grouped query images: when an area is selected from outside the canvas
 * (sidebar row, area-nav hotkeys, QA pager), show the view it was drawn on.
 * No-op for an ungrouped task.
 */
const showAreaView = (state: State, feEmptyAreaIdx: number): Partial<State> => {
  if (state.groupImages.length === 0) return {};
  const area = state.idxToFeEmptyArea[feEmptyAreaIdx];
  if (!area) return {};
  return showView(state, area.imageId ?? null, feEmptyAreaIdx);
};

export const useCoverageBetaStore = create<State>()((set) => ({
  ...initialState,
  setActiveProductId: (activeProductId) => set({ activeProductId }),
  setNeighbourProductIds: (neighbourProductIds) => set({ neighbourProductIds }),
  setLocatedProductId: (locatedProductId) => set({ locatedProductId }),
  requestPlanogramFocus: (productId) =>
    set((state) => ({
      planogramFocusRequest: {
        productId,
        nonce: (state.planogramFocusRequest?.nonce ?? 0) + 1,
      },
    })),
  clearPlanogramFocusRequest: () => set({ planogramFocusRequest: null }),
  setHoveredView: (hoveredView) => set({ hoveredView }),
  referencePinchRef: null,
  setReferencePinchRef: (referencePinchRef) => set({ referencePinchRef }),
  planogramPinchRef: null,
  setPlanogramPinchRef: (planogramPinchRef) => set({ planogramPinchRef }),
  resetPlanogramView: () =>
    set((s) => ({ planogramResetNonce: s.planogramResetNonce + 1 })),
  setBrightness: (view, value) =>
    set((s) => ({
      brightnessByView: {
        ...s.brightnessByView,
        [view]: clampBrightness(value),
      },
    })),
  adjustBrightness: (view, delta) =>
    set((s) => ({
      brightnessByView: {
        ...s.brightnessByView,
        [view]: clampBrightness(s.brightnessByView[view] + delta),
      },
    })),
  resetBrightness: (view) =>
    set((s) => ({
      brightnessByView: { ...s.brightnessByView, [view]: 100 },
    })),
  recordInteraction: (action, source) =>
    set((s) => {
      const prev = s.interactionCounts[action] ?? { hotkey: 0, click: 0 };
      return {
        interactionCounts: {
          ...s.interactionCounts,
          [action]: { ...prev, [source]: prev[source] + 1 },
        },
      };
    }),
  recordEvent: (name) =>
    set((s) => ({ eventCounts: incEvent(s.eventCounts, name) })),
  setActiveEmptyAreaIdx: (activeEmptyAreaIdx) => set({ activeEmptyAreaIdx }),
  prevEmptyArea: () => set((s) => stepEmptyArea(s, -1)),
  nextEmptyArea: () => set((s) => stepEmptyArea(s, 1)),
  setIdxToFeEmptyArea: (idxToFeEmptyArea) =>
    set((state) => ({
      idxToFeEmptyArea: flagSuggestionEdits(
        state.idxToFeEmptyArea,
        idxToFeEmptyArea,
      ),
    })),
  setActiveRefImgIdx: (activeRefImgIdx) => set({ activeRefImgIdx }),
  setReferenceImgCount: (referenceImgCount) => set({ referenceImgCount }),
  prevRefImg: () =>
    set((s) => ({ activeRefImgIdx: Math.max(0, s.activeRefImgIdx - 1) })),
  nextRefImg: () =>
    set((s) => ({
      activeRefImgIdx: Math.min(
        Math.max(0, s.referenceImgCount - 1),
        s.activeRefImgIdx + 1,
      ),
    })),
  setActiveMode: (activeMode) => set({ activeMode }),
  setRightPanelView: (rightPanelView) => set({ rightPanelView }),
  toggleRightPanelView: () =>
    set((s) => {
      // Nothing to toggle to without shelf images — the planogram is it.
      if (!s.hasShelfImages) return {};
      return {
        rightPanelView:
          s.rightPanelView === "planogram" ? "shelf-images" : "planogram",
      };
    }),
  hasShelfImages: true,
  setHasShelfImages: (hasShelfImages) => set({ hasShelfImages }),
  showSam2Boxes: false,
  toggleSam2Boxes: () => set((s) => ({ showSam2Boxes: !s.showSam2Boxes })),
  groupImages: [],
  primaryImageId: "",
  setImageGroup: (primaryImageId, groupImages) =>
    set({ primaryImageId, groupImages }),
  setActiveImageId: (activeImageId) =>
    set((s) => showView(s, activeImageId, null)),
  clearPendingAreaFocus: () => set({ pendingAreaFocusIdx: null }),
  setShowBoxes: (showBoxes) => set({ showBoxes }),
  toggleBoxes: () => set((s) => ({ showBoxes: !s.showBoxes })),
  setConfirming: (confirming) => set({ confirming }),
  nudgeConfirm: () =>
    set((state) =>
      state.confirming == null ? {} : { confirmNudge: state.confirmNudge + 1 },
    ),
  rejectConfirmingSelection: () =>
    set((state) => {
      if (state.isReadOnly) return {};
      const c = state.confirming;
      if (!c) return { confirming: null };
      const area = state.idxToFeEmptyArea[c.emptyAreaIdx];
      if (!area) return { confirming: null };
      const filtered = area.selectedProducts.filter(
        (p) =>
          !(
            p.source === "reference" &&
            p.referenceImgIdx === c.referenceImgIdx &&
            p.referenceProductDetectionIdx === c.referenceProductDetectionIdx
          ),
      );
      return {
        confirming: null,
        eventCounts: incEvent(state.eventCounts, "confirm_no_match"),
        idxToFeEmptyArea: {
          ...state.idxToFeEmptyArea,
          [c.emptyAreaIdx]: flagSuggestionEdit(area, {
            ...area,
            selectedProducts: filtered,
            // "No match" is a review decision: the area is valid (reviewed) even
            // when it ends up with no product, so it doesn't block submit.
            isValid: true,
            // The confirmation is rejected — drop any confirmed product.
            confirmedProductId: null,
          }),
        },
      };
    }),
  cancelConfirmingSelection: () =>
    set((state) => {
      if (state.isReadOnly) return {};
      const c = state.confirming;
      if (!c) return { confirming: null };
      const area = state.idxToFeEmptyArea[c.emptyAreaIdx];
      if (!area) return { confirming: null };
      const filtered = area.selectedProducts.filter(
        (p) =>
          !(
            p.source === "reference" &&
            p.referenceImgIdx === c.referenceImgIdx &&
            p.referenceProductDetectionIdx === c.referenceProductDetectionIdx
          ),
      );
      const emptied = filtered.length === 0;
      return {
        confirming: null,
        eventCounts: incEvent(state.eventCounts, "confirm_cancel"),
        idxToFeEmptyArea: {
          ...state.idxToFeEmptyArea,
          [c.emptyAreaIdx]: flagSuggestionEdit(area, {
            ...area,
            selectedProducts: filtered,
            // A cancel is an undo — if nothing remains, the area goes back to
            // un-reviewed (not marked valid like "No match").
            isValid: emptied ? false : area.isValid,
            // Cancel drops the confirmation being made.
            confirmedProductId: null,
          }),
        },
      };
    }),
  confirmReferenceProduct: (feEmptyAreaIdx, product) =>
    set((state) => {
      if (state.isReadOnly) return {};
      const c = state.confirming;
      const area = state.idxToFeEmptyArea[feEmptyAreaIdx];
      if (!area) return {};
      // Overwrite the detection being confirmed with the picked product,
      // preserving its detection indices, source, and original detected product
      // id (`...product` brings the new id/description/image_url; the fields
      // spread from `p` afterwards are the ones we must keep).
      const selectedProducts = area.selectedProducts.map((p) =>
        c != null &&
        p.source === "reference" &&
        p.referenceImgIdx === c.referenceImgIdx &&
        p.referenceProductDetectionIdx === c.referenceProductDetectionIdx
          ? {
              ...p,
              ...product,
              source: "reference" as const,
              referenceImgIdx: p.referenceImgIdx,
              referenceProductDetectionIdx: p.referenceProductDetectionIdx,
              identifiedProductId: p.identifiedProductId,
              confirmed: true,
            }
          : p,
      );
      return {
        confirming: null,
        eventCounts: incEvent(state.eventCounts, "confirm_pick"),
        idxToFeEmptyArea: {
          ...state.idxToFeEmptyArea,
          [feEmptyAreaIdx]: flagSuggestionEdit(area, {
            ...area,
            selectedProducts,
            confirmedProductId: product.id,
          }),
        },
      };
    }),
  addFeEmptyArea: (points, opts) =>
    set((state) => {
      if (state.isReadOnly) return {};
      const keys = Object.keys(state.idxToFeEmptyArea).map(Number);
      const newIdx = keys.length ? Math.max(...keys) + 1 : 0;
      return {
        idxToFeEmptyArea: {
          ...state.idxToFeEmptyArea,
          [newIdx]: {
            id: crypto.randomUUID(),
            points,
            class_index: opts?.class_index ?? 0,
            confidence: 1,
            source: "human",
            feEmptyAreaIdx: newIdx,
            isDeleted: false,
            selectedProducts: [],
            isHidden: false,
            isValid: false,
            imageId: state.activeImageId,
          },
        },
        // Make the freshly drawn area active so it can be categorized next.
        activeEmptyAreaIdx: newIdx,
        // Human-drawn areas have no aisle from the model → default to aisle 0.
        boxIdxToSlot: { ...state.boxIdxToSlot, [newIdx]: 0 },
      };
    }),
  deleteFeEmptyArea: (feEmptyAreaIdx) =>
    set((state) => {
      if (state.isReadOnly) return {};
      const area = state.idxToFeEmptyArea[feEmptyAreaIdx];
      if (!area) return {};
      // Only flip isDeleted — preserve products/confirmation/validity so a
      // restore is a true undo (the "all valid" gates ignore deleted areas).
      const idxToFeEmptyArea = {
        ...state.idxToFeEmptyArea,
        [feEmptyAreaIdx]: { ...area, isDeleted: true },
      };
      const patch: Partial<IS> = { idxToFeEmptyArea };
      // Don't keep a deleted area active/selected — move to a remaining one.
      if (state.activeEmptyAreaIdx === feEmptyAreaIdx) {
        const nextActive = Object.values(idxToFeEmptyArea).find(
          (a) => !a.isDeleted,
        );
        patch.activeEmptyAreaIdx = nextActive ? nextActive.feEmptyAreaIdx : 0;
      }
      // Close any confirm step targeting the deleted area.
      if (state.confirming?.emptyAreaIdx === feEmptyAreaIdx) {
        patch.confirming = null;
      }
      // The box unmounts to null on delete, so its hover-leave never fires —
      // clear any lingering neighbour highlight it set.
      if (state.neighbourProductIds.length) {
        patch.neighbourProductIds = [];
      }
      return patch;
    }),
  toggleEmptyAreaNoMatch: (feEmptyAreaIdx) =>
    set((state) => {
      if (state.isReadOnly) return {};
      const area = state.idxToFeEmptyArea[feEmptyAreaIdx];
      if (!area) return {};
      const isNoMatch = area.isValid && area.selectedProducts.length === 0;
      return {
        idxToFeEmptyArea: {
          ...state.idxToFeEmptyArea,
          [feEmptyAreaIdx]: flagSuggestionEdit(
            area,
            isNoMatch
              ? { ...area, isValid: false }
              : {
                  ...area,
                  isValid: true,
                  selectedProducts: [],
                  confirmedProductId: null,
                },
          ),
        },
      };
    }),
  addPerson: (points, opts) =>
    set((state) => {
      if (state.isReadOnly) return {};
      return {
        persons: [
          ...state.persons,
          {
            id: crypto.randomUUID(),
            points,
            confidence: 1,
            class_index: opts?.class_index ?? 0,
            isDeleted: false,
            imageId: state.activeImageId,
          },
        ],
      };
    }),
  setPersonDeleted: (id, isDeleted) =>
    set((state) => {
      if (state.isReadOnly) return {};
      return {
        persons: state.persons.map((p) =>
          p.id === id ? { ...p, isDeleted } : p,
        ),
      };
    }),
  updatePersonPoints: (id, points) =>
    set((state) => {
      if (state.isReadOnly) return {};
      return {
        persons: state.persons.map((p) => (p.id === id ? { ...p, points } : p)),
      };
    }),
  pinchRef: null,
  setPinchRef: (pinchRef) => set({ pinchRef }),
  tutorialActive: false,
  setTutorialActive: (tutorialActive) => set({ tutorialActive }),
  setBasePlanogramIds: (basePlanogramIds) => set({ basePlanogramIds }),
  setActivePlanogramTab: (activePlanogramTab) => set({ activePlanogramTab }),
  assignBoxToSlot: (feEmptyAreaIdx, slot) =>
    set((state) => {
      if (state.isReadOnly) return {};
      return {
        boxIdxToSlot: { ...state.boxIdxToSlot, [feEmptyAreaIdx]: slot },
      };
    }),
  applyPlanogramModalEdits: (slots) =>
    set((state) => {
      if (state.isReadOnly) return {};
      const overridePlanogramIds = { ...state.overridePlanogramIds };
      const extraPlanogramIds = [...state.extraPlanogramIds];
      const changedSlots = new Set<number>();

      for (const slot of slots) {
        const { aisleIndex, planogramId } = slot;
        if (aisleIndex >= 0 && aisleIndex < state.basePlanogramIds.length) {
          // Replace a base aisle's planogram (recorded as an override).
          const currentId =
            overridePlanogramIds[aisleIndex] ??
            state.basePlanogramIds[aisleIndex];
          if (planogramId && planogramId !== currentId) {
            overridePlanogramIds[aisleIndex] = planogramId;
            changedSlots.add(aisleIndex);
          }
        } else if (aisleIndex >= state.basePlanogramIds.length) {
          // Replace a previously-added "extra" planogram (its combined slot
          // index is basePlanogramIds.length + i).
          const extraIdx = aisleIndex - state.basePlanogramIds.length;
          if (
            planogramId &&
            extraIdx >= 0 &&
            extraIdx < extraPlanogramIds.length &&
            planogramId !== extraPlanogramIds[extraIdx]
          ) {
            extraPlanogramIds[extraIdx] = planogramId;
            changedSlots.add(aisleIndex);
          }
        } else if (aisleIndex === -1 && planogramId) {
          extraPlanogramIds.push(planogramId);
        }
      }

      // Replacing a slot's planogram invalidates its planogram-sourced picks.
      let idxToFeEmptyArea = state.idxToFeEmptyArea;
      if (changedSlots.size) {
        idxToFeEmptyArea = { ...state.idxToFeEmptyArea };
        for (const key of Object.keys(idxToFeEmptyArea)) {
          const idx = Number(key);
          if (!changedSlots.has(state.boxIdxToSlot[idx] ?? 0)) continue;
          const area = idxToFeEmptyArea[idx];
          const filtered = area.selectedProducts.filter(
            (p) => p.source !== "planogram",
          );
          if (filtered.length !== area.selectedProducts.length) {
            idxToFeEmptyArea[idx] = flagSuggestionEdit(area, {
              ...area,
              selectedProducts: filtered,
              isValid: filtered.length > 0 ? area.isValid : false,
            });
          }
        }
      }

      return { overridePlanogramIds, extraPlanogramIds, idxToFeEmptyArea };
    }),
  setIsReadOnly: (isReadOnly) => set({ isReadOnly }),
  hydrateReadOnly: (payload) =>
    set({
      idxToFeEmptyArea: payload.idxToFeEmptyArea,
      persons: payload.persons,
      basePlanogramIds: payload.basePlanogramIds,
      overridePlanogramIds: payload.overridePlanogramIds,
      extraPlanogramIds: payload.extraPlanogramIds,
      boxIdxToSlot: payload.boxIdxToSlot,
      activeEmptyAreaIdx: payload.activeEmptyAreaIdx,
      isReadOnly: true,
    }),
  hydrateEditable: (payload) =>
    set({
      idxToFeEmptyArea: payload.idxToFeEmptyArea,
      persons: payload.persons,
      basePlanogramIds: payload.basePlanogramIds,
      overridePlanogramIds: payload.overridePlanogramIds,
      extraPlanogramIds: payload.extraPlanogramIds,
      boxIdxToSlot: payload.boxIdxToSlot,
      activeEmptyAreaIdx: payload.activeEmptyAreaIdx,
      isReadOnly: false,
      isQaMode: false,
      qaEditOriginal: {
        idxToFeEmptyArea: payload.idxToFeEmptyArea,
        boxIdxToSlot: payload.boxIdxToSlot,
      },
    }),
  revertAreaToLabeler: (feEmptyAreaIdx) =>
    set((state) => {
      const original = state.qaEditOriginal;
      if (!original || state.confirming != null) return {};
      const labelerArea = original.idxToFeEmptyArea[feEmptyAreaIdx];
      if (labelerArea) {
        return {
          idxToFeEmptyArea: {
            ...state.idxToFeEmptyArea,
            [feEmptyAreaIdx]: labelerArea,
          },
          boxIdxToSlot: {
            ...state.boxIdxToSlot,
            [feEmptyAreaIdx]: original.boxIdxToSlot[feEmptyAreaIdx] ?? 0,
          },
        };
      }
      const idxToFeEmptyArea = omit(state.idxToFeEmptyArea, feEmptyAreaIdx);
      const patch: Partial<IS> = { idxToFeEmptyArea };
      if (state.activeEmptyAreaIdx === feEmptyAreaIdx) {
        const nextActive = Object.values(idxToFeEmptyArea).find(
          (a) => !a.isDeleted,
        );
        patch.activeEmptyAreaIdx = nextActive ? nextActive.feEmptyAreaIdx : 0;
      }
      return patch;
    }),
  setQaEditNote: (qaEditNote) => set({ qaEditNote }),
  setQaEditFlagLabeler: (qaEditFlagLabeler) => set({ qaEditFlagLabeler }),
  setQaMode: (isQaMode) => set({ isQaMode }),
  hydrateQaVerdicts: (qaVerdicts, qaBoxNotes, qaTaskLevelCorrections) =>
    set({ qaVerdicts, qaBoxNotes, qaTaskLevelCorrections }),
  setActiveQaItemKey: (activeQaItemKey) => set({ activeQaItemKey }),
  setActiveArea: (feEmptyAreaIdx) =>
    set((state) => ({
      ...(state.isQaMode
        ? { activeQaItemKey: `area:${feEmptyAreaIdx}` as QaItemKey }
        : { activeEmptyAreaIdx: feEmptyAreaIdx }),
      ...showAreaView(state, feEmptyAreaIdx),
    })),
  setQaBoxReasons: (key, reasons) =>
    set((state) => ({
      qaVerdicts: { ...state.qaVerdicts, [key]: reasons },
    })),
  setQaBoxNote: (key, note) =>
    set((state) => ({
      qaBoxNotes: { ...state.qaBoxNotes, [key]: note },
    })),
  setQaTaskLevelCorrections: (partial) =>
    set((state) => ({
      qaTaskLevelCorrections: { ...state.qaTaskLevelCorrections, ...partial },
    })),
  reset: () => set(initialState),
}));

/**
 * The empty area that everything outside the draw/edit flow (planogram
 * highlight/pulse, shelf-image match overlays, reference-detection
 * "selected" styling) should treat as active: in QA mode that's the pager's
 * area (`activeQaItemKey`'s `area:N`), or -1 when the pager is on a person
 * item (or hasn't been seeded yet) — never a real `feEmptyAreaIdx`, so every
 * `idxToFeEmptyArea[-1]` lookup downstream comes back `undefined` and nothing
 * highlights. Outside QA mode this is just `activeEmptyAreaIdx`. Structural
 * param (like `isQaReadyToSubmit`) so it composes inside a store selector and
 * unit-tests without a store.
 */
export const selectActiveAreaIdx = (s: {
  isQaMode: boolean;
  activeEmptyAreaIdx: number;
  activeQaItemKey: QaItemKey | null;
}): number => {
  if (!s.isQaMode) return s.activeEmptyAreaIdx;
  const key = s.activeQaItemKey;
  if (!key || !key.startsWith("area:")) return -1;
  return Number(key.slice("area:".length));
};

/**
 * The right panel's effective view: always the planogram when the task has no
 * shelf images (pilot), whatever `rightPanelView` says.
 */
export const selectRightPanelView = (s: {
  hasShelfImages: boolean;
  rightPanelView: "shelf-images" | "planogram";
}): "shelf-images" | "planogram" =>
  s.hasShelfImages ? s.rightPanelView : "planogram";

/** Whether a box (`imageId` null/absent = primary) sits on the shown view. */
export const isOnActiveView = (
  box: { imageId?: string | null },
  s: { activeImageId: string | null; primaryImageId: string },
): boolean =>
  (box.imageId ?? s.primaryImageId) === (s.activeImageId ?? s.primaryImageId);

/**
 * Whether an empty area counts as "active" — in QA mode that's driven by the
 * QA pager (`activeQaItemKey`), otherwise by the normal draw/edit flow's
 * `activeEmptyAreaIdx`. One shared selector so QueryEmptyAreaDetectionBox and
 * SummaryRow don't each re-derive the same mode-conditional check.
 */
export const useIsEmptyAreaActive = (feEmptyAreaIdx: number): boolean =>
  useCoverageBetaStore((s) => selectActiveAreaIdx(s) === feEmptyAreaIdx);

/**
 * Whether a person box is the QA pager's active item — person boxes have no
 * "active" concept outside QA mode. Shared by QueryPersonBox and
 * PersonSummaryRow.
 */
export const useIsPersonQaActive = (personIdx: number): boolean =>
  useCoverageBetaStore(
    (s) => s.isQaMode && s.activeQaItemKey === `person:${personIdx}`,
  );
